import { useQuery } from '@tanstack/react-query';
import type { APIResponseData } from 'common-types';
import { QueryParam, useParseParamFromQuery } from 'hooks/general/use-parse-param-from-query';
import { api } from 'services/api';
import { getArticles } from 'services/strapi/strapi';

import { insightsQueryKeys } from '../query-keys';

type TUseGetMostLikedArticlesParams = {
  count?: number;
  excludeArticleIds?: string[];
  profile?: string;
  enabled?: boolean;
};

type TUseGetMostLikedArticles = {
  data: APIResponseData<'api::article.article'>[];
  isPending: boolean;
  isError: boolean;
};

export const useGetMostLikedArticles = ({
  count = 4,
  excludeArticleIds,
  profile,
  enabled = true,
}: TUseGetMostLikedArticlesParams = {}): TUseGetMostLikedArticles => {
  const lang = useParseParamFromQuery(QueryParam.LANG);

  const { data, isPending, isError } = useQuery({
    queryKey: insightsQueryKeys.mostLikedArticles({ count, excludeIds: excludeArticleIds }),
    queryFn: async ({ signal }): Promise<APIResponseData<'api::article.article'>[]> => {
      const response = await api.getMostLikedArticles({ count, excludeArticleIds });
      const articleIds = response.mostLiked.insights;

      if (articleIds.length === 0) return [];

      const articles = await getArticles({
        filters: { id: { $in: articleIds } },
        pageSize: articleIds.length,
        locale: lang,
        signal,
        profile,
      });

      return articleIds
        .map((id) => articles.data.find((a) => String(a.id) === id))
        .filter((a): a is APIResponseData<'api::article.article'> => a !== undefined);
    },
    enabled,
  });

  return {
    data: data ?? [],
    isPending,
    isError,
  };
};
